/**
 * Pure, branded notebook-path domain. The single choke point for turning an
 * untrusted relative note path into a storage-addressable identity.
 *
 * A `NotebookPath` is a relative path under the per-tenant `notebook/` prefix
 * (e.g. `projects/idea.md`). Note identity IS the path — there are no synthetic
 * IDs anywhere (Obsidian-vault trajectory, spec §3.1). Because the path lands
 * in a GCS object key, the pod workspace filesystem, and the agent-facing tool
 * surface, validation is REJECT-only: we never rewrite a caller's path (that
 * would silently change identity). Contrast `convex/lib/sanitizeFilename.ts`,
 * whose job is to *rewrite* on the write path — here we replicate its character
 * classes purely and reject anything it would touch.
 *
 * Pure: no infra/convex imports, no `new Date()`, no `process.env`. Safe to
 * import from the Convex bundle and the browser bundle alike (spec §4.1).
 *
 * Folder-tree math over these paths (parent derivation, rebase, tree build)
 * lives in `domain/notebook-folders.ts` — folders are derived from note
 * paths, never stored.
 */

import type { Brand } from "./shared/types";
import type { Result } from "./shared/result";
import { err, ok } from "./shared/result";

/**
 * A validated, storage-safe relative notebook path. Storage calls are
 * type-impossible without having passed `validateNotebookPath` first.
 */
export type NotebookPath = Brand<string, "NotebookPath">;

export type NotebookPathError =
  | "empty_path"
  | "absolute_path"
  | "path_traversal"
  | "invalid_characters"
  | "not_markdown"
  | "path_too_long"
  | "prefix_escape";

/**
 * gcsfuse RW mount point inside the pod workspace (spec §3.3). Single-sourced
 * here so `claw-workspace.ts` and `gke.ts` import it rather than re-typing the
 * literal.
 */
export const NOTEBOOK_MOUNT_PATH = "/zeroclaw-data/workspace/notebook";

/** Object-key prefix under `${userId}/` for every note (spec §3.1). */
export const NOTEBOOK_PREFIX = "notebook/";

/** Hard caps (spec §4.1). */
const MAX_PATH_LENGTH = 512;
const MAX_SEGMENTS = 8;

/**
 * Character class `sanitizeFilename` (convex/lib) would rewrite, replicated
 * here as pure logic so the domain never imports `convex/lib` — with ONE
 * deliberate carve-out: the plain ASCII space (U+0020) is LEGAL in a notebook
 * segment (Obsidian-style names like `Research on X.md`, spec §3.1 / §4.1
 * "spaces legal"). Everything else sanitize would rewrite is rejected:
 *   `/`  U+0000-U+001F  U+007F  U+200B-U+200D  U+2060  and any NON-space `\s`.
 * `[^\\S ]` reads "a whitespace char that is not a plain space", so exotic
 * Unicode whitespace (U+00A0, U+202F narrow no-break space, U+2007, …) is still
 * rejected — precisely the chars an LLM cannot reliably reproduce (trace
 * c44fea0a) — while a genuine space survives. Written with explicit `\u`
 * escapes (never literal invisibles) so the class is unambiguous under review.
 */
const REJECT_CLASS = "/|[\\u0000-\\u001f]|\\u007f|[\\u200b-\\u200d]|\\u2060|[^\\S ]";
const SANITIZE_REWRITE_RE = new RegExp(`(?:${REJECT_CLASS})`, "u");

/**
 * The same class as a global run-matcher, for filename derivation (replace each
 * run of illegal chars with a single `-`).
 */
const DERIVE_REWRITE_RUN_RE = new RegExp(`(?:${REJECT_CLASS})+`, "gu");

/**
 * Percent-encoded traversal / separator sequences. An attacker can smuggle
 * `..%2f`, `%2e%2e/`, `%2e%2e%2f`, `%5c` (backslash) etc. — we reject any
 * percent-encoding rather than decode-then-revalidate (decoding could re-open
 * the traversal window). Case-insensitive because `%2F` == `%2f`.
 */
const PERCENT_ENCODED_RE = /%[0-9a-f]{2}/i;

/**
 * Normalize a raw path before validation (spec §4.1):
 *   - NFC (so composed vs decomposed forms collapse to one identity)
 *   - trim leading/trailing ASCII whitespace
 *   - collapse duplicate slashes
 *
 * Normalization is intentionally minimal: it does NOT strip `.`/`..` segments
 * or reorder — those are rejected downstream, never silently repaired.
 */
function normalizePath(path: string): string {
  return path.normalize("NFC").trim().replace(/\/{2,}/g, "/");
}

/**
 * Validate an untrusted relative notebook path into a branded `NotebookPath`.
 * Reject-only: on any violation returns `Err`, never a rewritten path.
 */
export function validateNotebookPath({
  path,
}: {
  path: string;
}): Result<NotebookPath, NotebookPathError> {
  if (typeof path !== "string" || path.length === 0) {
    return err("empty_path");
  }

  // Reject percent-encoding before normalization so smuggled traversal never
  // survives to the segment checks (`..%2f`, `%2e%2e/x.md`).
  if (PERCENT_ENCODED_RE.test(path)) {
    return err("path_traversal");
  }

  // Backslash is neither a legal separator nor a legal filename char here;
  // reject before normalization (it survives NFC untouched anyway).
  if (path.includes("\\")) {
    return err("invalid_characters");
  }

  const normalized = normalizePath(path);

  if (normalized.length === 0) {
    return err("empty_path");
  }
  if (normalized.length > MAX_PATH_LENGTH) {
    return err("path_too_long");
  }

  // Absolute after normalization → reject (a `//abs` collapses to `/abs`).
  if (normalized.startsWith("/")) {
    return err("absolute_path");
  }

  const segments = normalized.split("/");

  if (segments.length > MAX_SEGMENTS) {
    return err("path_too_long");
  }

  for (const segment of segments) {
    // Empty segment: trailing slash, or (post-collapse) a lone-slash artifact.
    if (segment.length === 0) {
      return err("path_traversal");
    }
    // Traversal / self-reference segments.
    if (segment === "." || segment === "..") {
      return err("path_traversal");
    }
    // Any character the sanitizer would rewrite → reject (never rewrite).
    if (SANITIZE_REWRITE_RE.test(segment)) {
      return err("invalid_characters");
    }
  }

  // Only the final segment carries the extension; every note is markdown.
  const last = segments[segments.length - 1];
  if (!last.toLowerCase().endsWith(".md")) {
    return err("not_markdown");
  }

  return ok(normalized as NotebookPath);
}

/**
 * Derive the bucket object key for a validated notebook path (spec §3.1):
 * `${userId}/notebook/${path}`. The ONLY place this key is composed — callers
 * can never supply a bucket-absolute path. The `${userId}/` prefix is kept even
 * inside per-tenant buckets (D1 pattern, matches all existing mounts).
 */
export function resolveNotebookObjectKey({
  userId,
  path,
}: {
  userId: string;
  path: NotebookPath;
}): string {
  return `${userId}/${NOTEBOOK_PREFIX}${path}`;
}

/**
 * Human-readable title from a path: the final segment with its `.md` extension
 * stripped (spec §4.1). Title is display-only; the path remains identity.
 */
export function titleFromPath({ path }: { path: NotebookPath }): string {
  const segments = path.split("/");
  const last = segments[segments.length - 1];
  return last.replace(/\.md$/i, "");
}

/**
 * Derive an Obsidian-style `.md` filename from a human title (spec §4.1).
 * Spaces are legal; the result is NFC-normalized and any run of characters the
 * sanitizer would rewrite (control / zero-width / exotic whitespace / slash) is
 * replaced with a single `-` — a filename derivation IS a rewrite surface,
 * distinct from path validation, which rejects. Ordinary ASCII spaces are
 * preserved (collapsed). Collision handling is UI-side; the server is
 * create-or-overwrite.
 */
export function deriveNoteFileName({
  title,
}: {
  title: string;
}): Result<string, NotebookPathError> {
  const normalized = title.normalize("NFC").trim();
  if (normalized.length === 0) {
    return err("empty_path");
  }

  // Rewrite runs of illegal chars (control / zero-width / exotic whitespace /
  // slash) to a single `-`, preserving plain ASCII spaces; then collapse space
  // and hyphen runs and trim edge separators.
  const cleaned = normalized
    .replace(DERIVE_REWRITE_RUN_RE, "-")
    .replace(/ {2,}/g, " ")
    .replace(/-{2,}/g, "-")
    .replace(/^[-\s]+|[-\s]+$/g, "");

  if (cleaned.length === 0) {
    return err("invalid_characters");
  }

  const fileName = cleaned.toLowerCase().endsWith(".md")
    ? cleaned
    : `${cleaned}.md`;

  if (fileName.length > MAX_PATH_LENGTH) {
    return err("path_too_long");
  }

  return ok(fileName);
}
